/**
 * FEATURES — centrale catalogus van alle "nieuwe features" die we in deze app hebben.
 * Gebruikt in:
 *   - InstellingenPage "Functies"-tab (ON/OFF toggle + rol-selectie + uitleg)
 *   - useFeature()-hook om elders in de app te checken of een feature zichtbaar mag zijn
 *
 * Rollen: 'admin' | 'user' | 'alleen-lezen'
 *   - min_rol bepaalt welke rol-niveau toegang heeft
 *     (admin > user > alleen-lezen)
 */

export const FEATURES = [
  {
    key: 'ean_artikel',
    label: 'EAN-code op artikelen',
    beschrijving: 'Voeg EAN-13/Code-128 barcodes toe aan artikelen voor snelle identificatie en optionele weergave op facturen (per artikel instelbaar).',
    voordelen: 'Sneller zoeken via scanning, B2B-klanten kunnen barcodes op hun inkomende facturen matchen.',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'artikelen',
  },
  {
    key: 'ean_generator',
    label: 'EAN-13 auto-generator',
    beschrijving: 'Vraag-banner bij nieuw artikel ("Automatisch genereren?") + "Genereer"-knop die een geldige EAN-13 met checksum produceert (prefix 20 = intern gebruik, zonder GS1-registratie).',
    voordelen: 'Eigen producten zonder fabrieksbarcode toch barcodeerbaar maken.',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'artikelen',
  },
  {
    key: 'barcode_scanner',
    label: 'Camera-barcode scanner',
    beschrijving: 'Live scannen via smartphone/laptop-camera op Artikelen-form én in factuurregels (auto-invoegen artikel op basis van EAN; dubbele scans verhogen het aantal voor kassa-workflow).',
    voordelen: 'Tot 10× snellere inventaris + kassa-ticketing, geen typefouten.',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'artikelen',
  },
  {
    key: 'labels_export',
    label: 'Barcode-labels exporteren',
    beschrijving: 'Exporteer barcode-labels in 10+ formaten (Dymo 99012, Brother DK-1201/DK-1202, Zebra, Avery L7159/L7160/L7163, of custom). Inclusief art_code, omschrijving, prijs, kopieën per artikel.',
    voordelen: 'Direct printen op rolprinters of A4-vellen, fysieke voorraad labelen in minuten.',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'artikelen',
  },
  {
    key: 'voorraad_auto_sync',
    label: 'Automatische voorraad-aftrek bij verkoop',
    beschrijving: 'Voorraad wordt automatisch verlaagd bij factuur/leverbon/bestelbon met actieve status en bij kassa-tickets. Creditnota\'s geven terug. Bij verwijderen van een document wordt de beweging geannuleerd (idempotent via snapshot).',
    voordelen: 'Live voorraadtelling zonder manuele updates; basis voor POS-flow.',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'voorraad',
  },
  {
    key: 'voorraad_waarschuwing',
    label: 'Kritieke-voorraad waarschuwing op dashboard',
    beschrijving: 'Dashboard-widget toont alle artikelen onder hun min_voorraad, gegroepeerd per leverancier, met suggestie-hoeveelheid (2× min − huidig).',
    voordelen: 'Voorraadtekorten op één oogopslag zichtbaar; voorkomt "out of stock"-situaties.',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'voorraad',
  },
  {
    key: 'multi_leverancier',
    label: 'Voorkeurs-leveranciers per artikel',
    beschrijving: 'Artikel kan meerdere leveranciers hebben met elk hun aankoopprijs, levertijd en MOQ. Systeem kiest automatisch de goedkoopste+snelste als primaire; alternatieven zichtbaar in bestelmodal met 1-klik-schakel-optie.',
    voordelen: 'Optimale inkoop voor KMO\'s die bij meerdere groothandels kopen; transparante prijsvergelijking.',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'leveranciers',
  },
  {
    key: 'po_aanmaken',
    label: 'Aankoopbestellingen aanmaken',
    beschrijving: '"Bestel bij leverancier"-knop per leverancier-groep in kritieke-voorraad widget → modal om bestelling samen te stellen, voorgevulde aantallen en prijzen, PDF-preview.',
    voordelen: 'Van voorraadalarm naar verzonden bestelling in 30 seconden.',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'leveranciers',
  },
  {
    key: 'po_email_verzenden',
    label: 'PO automatisch per e-mail verzenden',
    beschrijving: 'Via SendGrid naar het e-mailadres van de leverancier; HTML-PDF als bijlage, bevestigingsdeadline (standaard 48u) en publieke link waar de leverancier de bestelling online kan bevestigen of aanpassen.',
    voordelen: 'Minder heen-en-weer mailen; leverancier-antwoord gestructureerd bijgehouden.',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'leveranciers',
  },
  {
    key: 'po_publiek_bevestig',
    label: 'Publieke bevestigingspagina voor leveranciers',
    beschrijving: 'Leverancier krijgt een unieke /bevestig-bestelling/{token}-URL (geen login). Hij kan de bestelling bevestigen, of aantallen/prijzen per regel aanpassen met optioneel bericht.',
    voordelen: 'Basis voor automatische 3-weg matching (PO ↔ levering ↔ factuur).',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'leveranciers',
  },
  {
    key: 'onboarding_wizard',
    label: 'Onboarding-wizard bij eerste login',
    beschrijving: '5-staps welkomstscherm dat nieuwe gebruikers kennis laat maken met alle functies via mini-demos en live voorkeuren-keuze. Start automatisch bij eerste bezoek; later opnieuw oproepbaar vanuit Instellingen.',
    voordelen: 'Snellere adoptie; gebruikers ontdekken functies die ze anders zouden missen.',
    default_actief: true,
    default_min_rol: 'alleen-lezen',
    categorie: 'onboarding',
    toegevoegd_in_versie: '2026.02.19',
  },
  {
    key: 'whats_new_banner',
    label: 'What\'s New-banner na updates',
    beschrijving: 'Gele banner bovenaan het dashboard die automatisch verschijnt wanneer een nieuwe versie van de app nieuwe functies bevat. Toont een korte bullet-lijst + "Start mini-tour"-knop die enkel de nieuwe functies uitlegt.',
    voordelen: 'Gebruikers blijven op de hoogte zonder release-notes te moeten lezen; lost "ik wist niet dat dit bestond" op.',
    default_actief: true,
    default_min_rol: 'alleen-lezen',
    categorie: 'onboarding',
    toegevoegd_in_versie: '2026.02.19',
  },
  {
    key: 'kassa_split_payment',
    label: 'Gesplitste betalingen aan kassa',
    beschrijving: 'Een ticket betaalbaar met combinatie van Contant, Bancontact, Visa en Maestro — elk met eigen bedrag dat optelt tot het totaal. Ook de restwisselweergave voor contant werkt automatisch.',
    voordelen: 'Meer flexibiliteit voor klanten die deels cash, deels kaart betalen; exact trackbaar per betaalwijze in het Z-rapport.',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'kassa',
    toegevoegd_in_versie: '2026.02.20',
  },
  {
    key: 'kassa_z_rapport',
    label: 'Z-rapport & maandoverzicht',
    beschrijving: 'Dagafsluiting met totalen per betaalwijze (contant, bancontact, visa, maestro) en een maandoverzicht met tickets per dag. Exporteerbaar naar bonprinter/PDF.',
    voordelen: 'Verplichte fiscale dagafsluiting voor BE-retail; snel zicht op omzet-mix.',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'kassa',
    toegevoegd_in_versie: '2026.02.20',
  },
  {
    key: 'kassa_winst_admin',
    label: 'Winstmarge enkel voor admins',
    beschrijving: 'Kostprijs × aantal wordt per ticket opgeslagen; Z-rapport en maandoverzicht tonen winst + marge % — maar enkel aan accounts met rol "admin" of recht "mag_winst_zien". Gewone kassa-medewerkers zien alleen de omzet.',
    voordelen: 'Voorkomt dat personeel inkoopprijzen ziet; voldoet aan interne data-scheiding.',
    default_actief: true,
    default_min_rol: 'admin',
    categorie: 'kassa',
    toegevoegd_in_versie: '2026.02.20',
  },
  {
    key: 'kassa_dag_naar_factuur',
    label: 'Dagomzet bundelen tot één factuur',
    beschrijving: '"Dag naar factuur"-knop combineert alle kassa-tickets van een dag tot één factuur op de systeem-klant "Kassaverkoop" (artikel-regels samengevoegd per art_id). Ideaal voor boekhoudexport.',
    voordelen: 'Eén lijn in de boekhouding i.p.v. honderden kassa-tickets; verbindt retail en facturatie naadloos.',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'kassa',
    toegevoegd_in_versie: '2026.02.20',
  },
  {
    key: 'kassa_klant_display',
    label: 'Klant-display (2e scherm)',
    beschrijving: 'Publiek scherm dat u opent op een tablet of 2e monitor bij de kassa. Live sync van artikels, totaal, betaalstatus. Tijdens het afrekenen wordt automatisch een SEPA-betaal-QR getoond — klant kan scannen met zijn bankapp.',
    voordelen: 'Echte retail-ervaring; klanten zien mee wat er gescand wordt, minder discussies aan de kassa, snellere betalingen via mobile banking.',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'kassa',
    toegevoegd_in_versie: '2026.02.21',
  },
  {
    key: 'kassa_display_branding',
    label: 'Klant-display branding & reclame',
    beschrijving: 'Personaliseer het klant-display met eigen welkomstekst, bedanktekst, kleurschema (6 voorinstellingen of custom) en roterende reclamebanners in idle-modus (upload uw eigen promo-afbeeldingen).',
    voordelen: 'Display voelt als uw eigen winkel; gebruik de idle-tijd voor promoties, nieuwe producten of openingsuren.',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'kassa',
    toegevoegd_in_versie: '2026.02.22',
  },
  {
    key: 'kassa_touch_ui',
    label: 'Touchscreen-vriendelijke kassa',
    beschrijving: 'Kassa-scherm volledig geoptimaliseerd voor tablet/touchscreen: artikels als grote tegels in een 2-5 kolommen grid, 44-64px tap-targets, snel-bedrag-knoppen (€5/10/20/50/100/200 + Exact) en betaalwijze-tiles met live-indicator.',
    voordelen: 'Geen muis nodig; kassier bedient de POS op iPad of touch-monitor met minder fouten en hogere snelheid.',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'kassa',
    toegevoegd_in_versie: '2026.02.23',
  },
  {
    key: 'kassa_ticket_branding',
    label: 'Kassaticket personaliseren',
    beschrijving: 'Stel de ticket-nummering (prefix, aantal cijfers, teller reset per dag ja/nee), een logo bovenaan (PNG/JPG/SVG/WEBP, 3 groottes), een header-regel, een voetekst en de verkoopvoorwaarden onderaan de kassabon in. Inclusief placeholders ({bedrijf}, {btw}, {iban}, {ticket}, {datum}…) en een live-preview.',
    voordelen: 'Juridisch in orde met duidelijke verkoopvoorwaarden + eigen branding (logo + eigen teksten) op elke bon. Teller per dag = klassieke retail-nummering.',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'kassa',
    toegevoegd_in_versie: '2026.02.24',
  },
  {
    key: 'doc_stroom_conversie',
    label: 'Documentenstroom & conversie-knoppen',
    beschrijving: 'Op elk document (offerte/proforma/bestelbon/leverbon/factuur) direct knoppen om het om te zetten naar een ander type + een live documentenstroom-sidebar die de volledige keten toont (met badges, status, handtekening-indicator, klikbare navigatie). Retroactief koppelen van losse documenten aan een brondocument ondersteund.',
    voordelen: 'Volledig traceerbare document-flow zonder achter details te zoeken; één-klik conversie van factuur → leverbon / bestelbon / creditnota.',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'boekhouding',
    toegevoegd_in_versie: '2026.02.25',
  },
  {
    key: 'leverbon_handtekening',
    label: 'Digitale handtekening op leverbon',
    beschrijving: 'Handtekening-pad op elke leverbon: klant tekent met vinger of stylus op uw tablet, of scan een 48u-geldige smartphone-link. Naam, tijdstip en IP-adres worden automatisch opgeslagen. Status wordt automatisch &laquo;geleverd&raquo;.',
    voordelen: 'Juridisch sterker bewijs van levering + paperless workflow. Geschikt voor afleveringen zonder tablet van de bestuurder (klant tekent op eigen smartphone).',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'boekhouding',
    toegevoegd_in_versie: '2026.02.25',
  },
  {
    key: 'nummering_harmonisatie',
    label: 'Nummering-harmonisatie tool',
    beschrijving: 'Controleer en herstel document-nummers wanneer u de padding-instelling aanpast nadat er al documenten bestaan. Zet oude 3-cijfer IDs om naar het huidige formaat, werkt cross-references (bron_id, betalingen, herinneringen) mee bij, lost duplicaten op met opvolgnummers.',
    voordelen: 'Consistente nummering over uw volledige documentensysteem; voorkomt duplicate-verwarring en onjuiste sortering.',
    default_actief: true,
    default_min_rol: 'admin',
    categorie: 'boekhouding',
    toegevoegd_in_versie: '2026.02.26',
  },
  {
    key: 'verzend_controle',
    label: 'Verzend-controle & cascade-sync',
    beschrijving: 'Op elk afgeleid document (bv. factuur uit leverbon) verschijnt een "Verzend-controle" paneel dat verschillen met het brondocument detecteert (klant, totaal, regels). Eén klik om bij te werken vanuit de bron; groene "Gecontroleerd"-stempel ter bevestiging vóór verzending. Bij wijziging van het brondocument wordt automatisch voorgesteld om concept-kinderen mee te updaten.',
    voordelen: 'Voorkomt dat een verkeerde klant of bedrag op de verzonden factuur terechtkomt; catches fouten waar ze nog corrigeerbaar zijn (concept-status).',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'boekhouding',
    toegevoegd_in_versie: '2026.02.27',
  },

  // ─── Integraties (actief/inactief — verbergt knoppen in de UI als uit) ─
  {
    key: 'int_sendgrid',
    label: 'SendGrid e-mail',
    beschrijving: 'Verstuur facturen/offertes rechtstreeks per e-mail. Zet uit als u documenten handmatig doorstuurt.',
    voordelen: 'Automatische verzending met tracking. Geen kopiëren-plakken meer.',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'integraties',
  },
  {
    key: 'int_stripe',
    label: 'Stripe betaallinks',
    beschrijving: 'Voeg "Betaal online"-link toe aan facturen (creditcard/Bancontact). Zet uit als u enkel overschrijvingen ontvangt.',
    voordelen: 'Klanten betalen sneller = minder herinneringen.',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'integraties',
  },
  {
    key: 'int_mollie',
    label: 'Mollie betaallinks',
    beschrijving: 'Alternatief voor Stripe: iDEAL, Bancontact, KBC, ING. Zet uit als u Stripe al gebruikt.',
    voordelen: 'Belgisch/NL-vriendelijke betaalmethoden.',
    default_actief: false,
    default_min_rol: 'user',
    categorie: 'integraties',
  },
  {
    key: 'int_twilio_sms',
    label: 'Twilio SMS/WhatsApp',
    beschrijving: 'Verstuur factuur/herinnering per SMS of WhatsApp. Vereist Twilio-account.',
    voordelen: 'SMS haalt 95% openingsrate — ideaal voor betalingsherinneringen.',
    default_actief: false,
    default_min_rol: 'user',
    categorie: 'integraties',
  },
  {
    key: 'int_ocr',
    label: 'AI OCR inkomende facturen',
    beschrijving: 'Sleep een PDF-factuur en de AI herkent leverancier/datum/bedragen. Gebruikt Emergent LLM key.',
    voordelen: 'Bespaart 3-5 min per inkomende factuur.',
    default_actief: true,
    default_min_rol: 'admin',
    categorie: 'integraties',
  },
  {
    key: 'int_codabox',
    label: 'CodaBox bankkoppeling',
    beschrijving: 'Automatische import van banktransacties via CODA-format (alle Belgische banken).',
    voordelen: 'Geen manuele reconciliatie meer.',
    default_actief: false,
    default_min_rol: 'admin',
    categorie: 'integraties',
  },
  {
    key: 'int_woocommerce',
    label: 'WooCommerce sync',
    beschrijving: 'Synchroniseer artikels + bestellingen met uw WooCommerce webshop.',
    voordelen: 'Eén plek voor voorraad; automatische bestelbon-creatie vanuit webshop.',
    default_actief: false,
    default_min_rol: 'admin',
    categorie: 'integraties',
  },

  // ─── Document-types zichtbaarheid ─
  {
    key: 'doc_offerte',
    label: 'Offertes',
    beschrijving: 'Verberg het hele "Offertes"-menu-item als u nooit offertes maakt (bv. enkel directe verkoop).',
    voordelen: 'Simpelere sidebar, minder verwarring voor uw team.',
    default_actief: true,
    default_min_rol: 'alleen-lezen',
    categorie: 'doc_types',
  },
  {
    key: 'doc_bestelbon',
    label: 'Bestelbonnen',
    beschrijving: 'Verberg "Bestelbonnen" als u direct van offerte naar factuur springt zonder bestelbon-stap.',
    voordelen: 'Sidebar-vereenvoudiging voor kleinere shops.',
    default_actief: true,
    default_min_rol: 'alleen-lezen',
    categorie: 'doc_types',
  },
  {
    key: 'doc_proforma',
    label: 'Proforma-facturen',
    beschrijving: 'Verberg het proforma-type als u het nooit gebruikt (B2C-shops gebruiken dit zelden).',
    voordelen: 'Minder doc-types = minder fouten bij type-keuze.',
    default_actief: true,
    default_min_rol: 'alleen-lezen',
    categorie: 'doc_types',
  },
  {
    key: 'doc_leverbon',
    label: 'Leverbonnen',
    beschrijving: 'Verberg "Leverbonnen" als u geen fysieke goederen levert (bv. enkel diensten).',
    voordelen: 'Sidebar-vereenvoudiging voor dienstverleners.',
    default_actief: true,
    default_min_rol: 'alleen-lezen',
    categorie: 'doc_types',
  },
  {
    key: 'doc_creditnota',
    label: 'Creditnota\'s',
    beschrijving: 'Verberg creditnota\'s als u geen retourbeleid heeft of deze via support afhandelt.',
    voordelen: 'Strakker overzicht; kritische documenten blijven (facturen).',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'doc_types',
  },
  {
    key: 'doc_cmr',
    label: 'CMR (internationale vrachtbrief)',
    beschrijving: 'Verberg CMR-knop als u niet internationaal transporteert.',
    voordelen: 'CMR is zelden gebruikt door KMO\'s; uit verbergt een hoop UI-ruis.',
    default_actief: true,
    default_min_rol: 'user',
    categorie: 'doc_types',
  },

  // ─── B2B Webshop ─
  {
    key: 'webshop_actief',
    label: 'Webshop (B2B online bestellen)',
    beschrijving: 'Klanten loggen in op /shop/login en plaatsen zelf bestellingen. Nieuwe orders komen binnen in de sidebar-pagina "Webshop-bestellingen". Wanneer uit: klanten kunnen niet inloggen op de shop en de sidebar-pagina wordt verborgen.',
    voordelen: '24/7 bestellen zonder telefoon/mail; klanten zien hun eigen prijzen (prijsgroep + kortingen) en bestelhistoriek.',
    default_actief: false,
    default_min_rol: 'admin',
    categorie: 'integraties',
    syncs_to_root: 'webshop_actief',  // mirror .actief naar top-level instellingen.webshop_actief
  },
];

export const FEATURE_CATEGORIES = {
  artikelen: { label: 'Artikelen & Barcodes', kleur: '#002FA7' },
  voorraad: { label: 'Voorraad & Dashboard', kleur: '#0A9A6B' },
  leveranciers: { label: 'Leveranciers & Bestellingen', kleur: '#B45309' },
  onboarding: { label: 'Onboarding & Hulp', kleur: '#7C3AED' },
  kassa: { label: 'Kassa & POS', kleur: '#DC2626' },
  boekhouding: { label: 'Boekhouding & Documenten', kleur: '#059669' },
  integraties: { label: 'Integraties (aan/uit)', kleur: '#2563EB' },
  doc_types: { label: 'Document-types zichtbaarheid', kleur: '#9333EA' },
};

export const ROL_OPTIES = [
  { key: 'admin', label: 'Enkel admin', beschrijving: 'Enkel beheerders zien/gebruiken deze functie.' },
  { key: 'user', label: 'Alle gebruikers', beschrijving: 'Admin + gewone gebruikers. Alleen-lezen-accounts niet.' },
  { key: 'alleen-lezen', label: 'Iedereen (incl. alleen-lezen)', beschrijving: 'Alle rollen zien deze functie (zicht-gewijs).' },
];

const ROL_WAARDE = { 'alleen-lezen': 1, 'verkoper': 2, 'user': 2, 'gebruiker': 2, 'admin': 3 };

/**
 * Controleer of een user-rol toegang heeft tot een feature op basis van min_rol.
 */
export function rolMagFeature(userRol, minRol) {
  return (ROL_WAARDE[userRol] || 0) >= (ROL_WAARDE[minRol] || 0);
}

/**
 * Merge default-config met settings-overrides uit /api/instellingen.features
 *
 * Voor features met `syncs_to_root` synchroniseren we ook de top-level
 * instellingen-veld (bv. `webshop_actief`) naar de `actief`-vlag, zodat de UI
 * de state van bestaande instellingen correct toont, ook als de gebruiker de
 * feature nog nooit via Functies-tab heeft gewijzigd.
 */
export function mergeFeatures(opgeslagen, rootInst = {}) {
  const out = {};
  FEATURES.forEach(f => {
    const o = (opgeslagen || {})[f.key] || {};
    let actief;
    if (typeof o.actief === 'boolean') {
      actief = o.actief;
    } else if (f.syncs_to_root && rootInst && typeof rootInst[f.syncs_to_root] === 'boolean') {
      // Seed vanuit legacy top-level veld
      actief = rootInst[f.syncs_to_root];
    } else {
      actief = f.default_actief;
    }
    out[f.key] = {
      actief,
      min_rol: o.min_rol || f.default_min_rol,
    };
  });
  return out;
}

/**
 * Extraheer top-level velden die gemirrord moeten worden vanuit features-object.
 * Return: { webshop_actief: bool, ... } — leeg als geen enkele feature.syncs_to_root heeft.
 */
export function extractRootMirrorFields(features) {
  const out = {};
  FEATURES.forEach(f => {
    if (f.syncs_to_root && features && features[f.key]) {
      out[f.syncs_to_root] = !!features[f.key].actief;
    }
  });
  return out;
}
